import { format } from 'date-fns';
import { ForwardedRef, forwardRef, useRef } from 'react';
import { useNavigate } from 'react-router';
import { FETCH_INVOICE_LIST } from '~/actions';
import { Box, Column, DataGrid, Text } from '~/commonComponents';
import { getShouldEnforcePermanentJobV2 } from '~/growthbook';
import { useAppSelector, useLoading } from '~/hooks';
import { currencyFormat } from '~/hooks/currencyFormat';
import { IInvoice, PaymentRecordType } from '~/models';
import { Colors } from '~/themes/colors';

interface IPaymentInvoiceTableProps {
  onScroll: () => void;
}

const getPaymentRecordTypeColors = (type: PaymentRecordType) => {
  switch (type) {
    case PaymentRecordType.ListingFee:
      return Colors.jobs_listing_fee_background;
    case PaymentRecordType.OnsiteInterview:
      return Colors.jobs_interview_background;
    default:
      return Colors.jobs_shift_background;
  }
};

const getPaymentRecordTypeText = (type: PaymentRecordType) => {
  switch (type) {
    case PaymentRecordType.ListingFee:
      return 'LISTING FEE';
    case PaymentRecordType.OnsiteInterview:
      return 'INTERVIEW';
    default:
      return 'SHIFT';
  }
};

export const PaymentInvoiceTable = forwardRef<HTMLDivElement, IPaymentInvoiceTableProps>(
  ({ onScroll }, ref: ForwardedRef<HTMLDivElement>) => {
    const navigate = useNavigate();

    const invoices: IInvoice[] = useAppSelector((state) => state.job.invoices);
    const { isLoading } = useLoading([FETCH_INVOICE_LIST]);

    const isEnforcePermanentJobV2 = useRef(getShouldEnforcePermanentJobV2()).current;

    return (
      <Column
        marginTop={32}
        maxHeight={600}
        overflowY="scroll"
        ref={ref}
        width="100%"
        onScroll={onScroll}
      >
        <DataGrid
          EmptyComponent={<Text textAlign="center">There are no invoices.</Text>}
          columns={[
            {
              key: 'view',
              header: isEnforcePermanentJobV2 ? 'View' : 'Shift',
              width: '140px',
              render: (item) =>
                isEnforcePermanentJobV2 ? (
                  <Box
                    alignSelf="flex-start"
                    backgroundColor={getPaymentRecordTypeColors(item.paymentRecordType)}
                    borderRadius={8}
                    cursor="pointer"
                    paddingHorizontal={10}
                    paddingVertical={2}
                    onClick={() => {
                      if (item.paymentRecordType === PaymentRecordType.ListingFee) {
                        navigate(`/permanent-jobs/job-overview/${item.job_id}`);
                        return;
                      }

                      navigate(`/dashboard/job/${item?.job_id}`, {
                        state: { job_id: item?.job_id, jobId: item?.job_id },
                      });
                    }}
                  >
                    <Text color="neutral_500" textDecoration="underline">
                      {getPaymentRecordTypeText(item.paymentRecordType)}
                    </Text>
                  </Box>
                ) : (
                  <Box
                    onClick={() => {
                      navigate(`/dashboard/job/${item?.job_id}`, {
                        state: { job_id: item?.job_id, jobId: item?.job_id },
                      });
                    }}
                  >
                    <Text color="neutral_500" textDecoration="underline">
                      View Shift
                    </Text>
                  </Box>
                ),
            },
            {
              key: 'office',
              header: 'Office',
              render: (item) => <Text>{item?.office_name}</Text>,
            },
            {
              key: 'amount',
              header: 'Amount',
              width: '140px',
              render: (item) => <Text bold>${currencyFormat(item.amount)}</Text>,
            },
            {
              key: 'posting_date',
              header: isEnforcePermanentJobV2 ? 'Posting Date' : 'Shift Date',
              width: '200px',
              render: (item) => (
                <Text>
                  {item.jobDate ? format(new Date(item.jobDate), 'EEE, MMM dd, yyyy') : '-'}
                </Text>
              ),
            },
            {
              key: 'invoice_date',
              header: 'Invoice Date',
              width: '200px',
              render: (item) => (
                <Column>
                  <Text>{item.date ? format(new Date(item.date), 'EEE, MMM dd, yyyy') : '-'}</Text>
                  <Text>{item.date ? format(new Date(item.date), 'hh:mm a') : '-'}</Text>
                </Column>
              ),
            },
            {
              key: 'invoice_#',
              header: 'Invoice #',
              width: '140px',
              render: (item) => (
                <a href={item.invoice_url} target="_blank" rel="noopener noreferrer">
                  <Text bold color={Colors.secondary_500} fontSize={15} textDecoration="underline">
                    {item.invoice_number || '-'}
                  </Text>
                </a>
              ),
            },
          ]}
          data={invoices}
          keyExtractor={(it) => it.transaction_id}
          isLoading={isLoading}
          isStickyHeader
        />
      </Column>
    );
  },
);

PaymentInvoiceTable.displayName = 'PaymentInvoiceTable';
